import React from "react";
import { TextNode, View, Image } from "@surge-global-engineering/react-pdf-components";
import { getNormalizedOpacity } from "@surge-global-engineering/css-generator";
import { StyleSheet } from "@surge-global-engineering/rpdf-renderer";
import { HeaderImage, PrintExtraProps } from "@surge-global-engineering/css-generator";
import {
  themeProperties as preBuildThemeProperties,
} from "../configs/prebuilt-theme-properties";
import {
  PdfChapter,
  PdfTheme,
  PdfThemeProperties,
} from "../types";
import {
  centimetersToPt,
  getChapterNumberLabel,
  chapterNumberToTitleCase,
} from "../helpers";
import { getThemeConfigsForChapterTypes } from "../configs";
import {
  getContainerStyle,
  getHeaderSelectorStyle,
  getContentAreaHeight,
  getChapterHeaderFontStyles
} from "../helpers/common";

interface TextStylesProperties {
  font: string;
  size: number;
  align: "left" | "center" | "right";
  style: IThemeStore.HeaderFontStyle[];
  width: number;
}

type TitleType = "chapterTitle" | "chapterNo" | "chapterSubtitle"

const getDefaultStyles = (baseFontSize: number, containerMinHeight?: number) => {
  return StyleSheet.create({
    container: {
      minHeight: containerMinHeight ? `${Math.round(containerMinHeight)}pt` : baseFontSize * 13,
      paddingTop: baseFontSize * 1,
      paddingBottom: baseFontSize * 1,
      position: "relative",
    },
    chapterTitle: {
      fontSize: baseFontSize * 2,
      textAlign: "left",
      lineHeight: 1.2,
      width: "100%",
      paddingTop: baseFontSize * 0.4,
      paddingBottom: baseFontSize * 0.4,
    },
    chapterSubTitle: {
      fontSize: baseFontSize * 1.6666666666666667,
      textAlign: "left",
      lineHeight: 1.2,
      width: "100%",
      paddingTop: baseFontSize * 0.4,
      paddingBottom: baseFontSize * 0.4,
    },
    chapterNumber: {
      fontSize: baseFontSize * 1.3333333333333333,
      textAlign: "left",
      lineHeight: 1.2,
      width: "100%",
      paddingBottom: baseFontSize * 0.4,
    },
    titleImageContainer: {
      paddingTop: baseFontSize * 0.3,
      paddingBottom: baseFontSize * 0.3,
    },
    marginImageContainer: {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
    },
  });
};

export interface ChapterTitleCardProps {
  chapter: PdfChapter;
  theme: PdfTheme;
  chapterNumber?: number;
}
const MIN_HEIGHT_OFFSET = 50;
const ChapterTitleCard: React.FC<ChapterTitleCardProps> = ({
  chapter,
  theme,
  chapterNumber,
}) => {
  const { title, subtitle, type, configuration } = chapter;
  const { showChapterImage = true } = configuration || {};
  const { properties: themeProps } = theme;
  const { chapterNumbering, titleCard, baseFontSize, image, chapterTitle, chapterSubtitle, chapterNo } = themeProps;
  const {
    chapterNumber: ischapterNumberVisible,
    title: isTitleVisible,
    subtitle: isSubtitleVisible,
    image: isTitleImageVisible,
  } = titleCard;

  const { firstSentenceShiftDownMultiplier } = getThemeConfigsForChapterTypes(type);

  const defaultChapterStyles = getDefaultStyles(baseFontSize,
    firstSentenceShiftDownMultiplier ? ((getContentAreaHeight(themeProps) * firstSentenceShiftDownMultiplier) - MIN_HEIGHT_OFFSET) : undefined);

  const ChapterImage = () => {
    if ((!isTitleImageVisible || !showChapterImage)) return <></>;
    if (
      !image ||
      (image && image.placement === "background-image") ||
      (image && !image.url)
    ) {
      return null;
    }

    const { colored, url, alignment, width } = image;
    if (!url) return <></>;

    // TODO: Implement colored or grayscale image
    return (
      <View style={defaultChapterStyles.titleImageContainer}>
        <Image src={url} alignment={alignment} width={width} />
      </View>
    );
  };

  const ChapterMarginImage = () => {
    if (!isTitleImageVisible || !showChapterImage) return <></>;
    const _images = image;
    if (
      !_images ||
      (_images && _images.placement !== "background-image") ||
      (_images && !_images.url) ||
      (_images && _images.printExtent !== "margins")
    ) {
      return null;
    }
    const { colored, url, opacity } = _images;
    if (!url) return <></>;
    // TODO: Implement colored or grayscale image
    return (
      <View
        style={[
          defaultChapterStyles.marginImageContainer,
          image.placement === "above-chapter-no" ? { paddingTop: 0 } : {},
        ]}
      >
        <Image src={url} style={{ width: "100%", opacity: getNormalizedOpacity(opacity)}} />
      </View>
    );
  };
  const { showDefaultTextColour = true } = chapter?.configuration || {};
  const { headerTextColor } = theme.properties.image;

  const hyphenationCallback = (word: string) => [word];
  const lightText =(showDefaultTextColour && headerTextColor === "light") ||(!showDefaultTextColour && headerTextColor === "default");

  return (
    <View style={[getContainerStyle(themeProps, type)]}>
      <ChapterMarginImage />
      {image.placement === "above-chapter-no" && <ChapterImage />}
      <TextNode
        {...getChapterHeaderFontStyles("chapterNo", themeProps)}
        style={[
          getHeaderSelectorStyle(
            "chapterNo",
            themeProps,
            chapter,
            lightText
          ),
          !ischapterNumberVisible
            ? {
                borderBottom: 0,
                borderTop: 0,
              }
            : {},
        ]}
        hyphenationCallback={hyphenationCallback}
      >
        {ischapterNumberVisible
          ? chapterNumberToTitleCase(
              getChapterNumberLabel(chapterNumbering, chapterNumber) || ""
            )
          : ""}
      </TextNode>
      {image.placement === "above-chapter-title" && <ChapterImage />}
      <TextNode
        {...getChapterHeaderFontStyles("chapterTitle", themeProps)}
        style={[
          getHeaderSelectorStyle(
            "chapterTitle",
            themeProps,
            chapter,
            lightText
          ),
        ]}
        hyphenationCallback={hyphenationCallback}
      >
        {isTitleVisible ? title : ""}
      </TextNode>
      {image.placement === "below-chapter-title" && <ChapterImage />}
      <TextNode
        {...getChapterHeaderFontStyles("chapterSubtitle", themeProps)}
        style={[
          getHeaderSelectorStyle(
            "chapterSubtitle",
            themeProps,
            chapter,
            lightText
          ),
        ]}
        hyphenationCallback={hyphenationCallback}
      >
        {isSubtitleVisible ? subtitle : ""}
      </TextNode>
      {image.placement === "below-subtitle" && <ChapterImage />}
    </View>
  );
};

export default ChapterTitleCard;
